import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Zap, Mountain, Anchor, Factory, Wheat, Server, Coins, Landmark, Check } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import BriefingRequestModal from '../../components/BriefingRequestModal';
import { strategy } from '../../data/strategy';
import { MotionReveal } from '../../components/strategy/Motion';
import { PlatformDonut } from '../../components/strategy/Charts';

const ICONS = [Zap, Mountain, Anchor, Factory, Wheat, Server, Coins, Landmark];

const Platforms = () => {
  const [audience, setAudience] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="App font-sans bg-white">
      <Header onPartnerClick={() => setAudience('capital')} />

      <section className="pt-32 pb-16 px-6 bg-[#0a1729] text-white">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8" data-testid="platforms-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#7fd6c9] mb-3">Eight Strategic Platforms</div>
          <h1 className="font-serif text-4xl md:text-6xl font-bold leading-tight max-w-4xl">
            One integrated operating system across the African opportunity.
          </h1>
          <p className="text-lg text-white/70 max-w-3xl mt-6">
            BIG operates through eight permanent-capital platforms, three cross-cutting systems and a unified financial engine. Each platform integrates with the others through shared infrastructure, capital and intelligence.
          </p>
        </div>
      </section>

      {/* Donut: capital allocation across platforms */}
      <section className="py-12 px-6 bg-[#08111f] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <MotionReveal className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#7fd6c9] mb-3">Capital Allocation Across Platforms</div>
              <h2 className="font-serif text-2xl md:text-3xl font-bold leading-tight mb-4">
                $500B distributed across eight permanent-capital platforms.
              </h2>
              <p className="text-sm text-white/65 leading-relaxed mb-5">
                Energy + critical minerals + logistics together represent the spine of the platform — close to 60% of cumulative deployment. The remaining engines compound from this base.
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {strategy.platforms.filter(p => p.target.startsWith('$')).map((p, i) => {
                  const colors = ['#2f9e8e', '#7fd6c9', '#c19a3c', '#e0c674', '#5dbfb0', '#a8842f', '#1a365d', '#3b82a6'];
                  return (
                    <div key={p.code} className="flex items-center gap-2 text-xs text-white/80">
                      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: colors[i] }} />
                      <span className="flex-1 truncate">{p.code}</span>
                      <span className="font-bold text-white">{p.target}</span>
                    </div>
                  );
                })}
              </div>
            </MotionReveal>
            <MotionReveal className="lg:col-span-7" delay={120}>
              <PlatformDonut
                data={strategy.platforms.filter(p => p.target.startsWith('$')).map((p) => ({ name: p.code, value: parseInt(p.target.replace(/[^0-9]/g, ''), 10) }))}
                height={360}
              />
            </MotionReveal>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto space-y-12">
          {strategy.platforms.map((p, i) => {
            const Icon = ICONS[i] || Coins;
            return (
              <MotionReveal key={p.code} delay={i * 50}>
                <div className="grid md:grid-cols-12 gap-6 pb-12 border-b border-gray-100 last:border-0">
                <div className="md:col-span-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-lg bg-[#1a365d] flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[#7fd6c9]" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#2f9e8e]">{p.code}</div>
                      <div className="text-2xl font-bold text-[#1a365d]">{p.target}</div>
                    </div>
                  </div>
                  <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#1a365d] mb-2">{p.title}</h2>
                  <p className="text-gray-600 leading-relaxed">{p.tagline}</p>
                  <div className="mt-4 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-[#e8f5f3] text-[#268578] font-semibold">
                    Target return: {p.returns}
                  </div>
                </div>
                <div className="md:col-span-8">
                  <ul className="grid sm:grid-cols-2 gap-2.5">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 p-4 bg-[#f6f9fb] rounded-lg">
                        <Check className="w-4 h-4 text-[#2f9e8e] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-800">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                </div>
              </MotionReveal>
            );
          })}
        </div>
      </section>

      <section className="py-16 px-6 bg-[#f6f9fb]">
        <div className="max-w-7xl mx-auto">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#2f9e8e] mb-3">Cross-cutting Systems</div>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1a365d] mb-10 max-w-3xl">
            Three operating systems that compound advantage across every platform.
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {strategy.crossCutting.map((x) => (
              <div key={x.name} className="bg-white p-7 rounded-xl border border-gray-100">
                <h3 className="font-serif text-xl font-bold text-[#1a365d] mb-3">{x.name}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{x.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-[#0a1729] text-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6">Ready to engage on a platform?</h2>
          <div className="flex flex-wrap gap-3 justify-center">
            <button onClick={() => setAudience('capital')} className="bg-[#2f9e8e] hover:bg-[#268578] px-6 py-3 rounded-lg font-semibold" data-testid="platforms-cta-capital">Capital Partners <ArrowRight className="inline w-4 h-4 ml-1" /></button>
            <button onClick={() => setAudience('sovereign')} className="bg-[#c19a3c] hover:bg-[#a8842f] px-6 py-3 rounded-lg font-semibold">Sovereign <ArrowRight className="inline w-4 h-4 ml-1" /></button>
            <button onClick={() => setAudience('corporate')} className="bg-white/10 hover:bg-white/20 border border-white/20 px-6 py-3 rounded-lg font-semibold">U.S. Corporate <ArrowRight className="inline w-4 h-4 ml-1" /></button>
          </div>
        </div>
      </section>

      <Footer />
      {audience && <BriefingRequestModal isOpen={!!audience} onClose={() => setAudience(null)} audience={audience} />}
    </div>
  );
};

export default Platforms;
